import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { COMPANY_LEGAL_RIGHT_API_SERVICE_TOKEN } from '@nk/api-lib';
import { CompanyLegalRightCompositeEntity, PersonEntity } from '@nk/data-entities-lib';
import { TextToPersonLinkPipe } from '@nk/person-block';
import { DateRangeComponent } from '@nk/ui-lib';
import { DateFormats, LOADING_STATE_HANDLER_SERVICE_TOKEN } from '@nk/utils-lib';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { ScreeningInputParamsService } from '../../services/screening-input-params.service';

@Component({
  selector: 'nk-screening-report-company-legal-right',
  imports: [AsyncPipe, TranslateModule, DateRangeComponent, TextToPersonLinkPipe],
  templateUrl: './screening-report-company-legal-right.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningReportCompanyLegalRightComponent implements OnInit {
  public screeningEntitiesInputParams = computed(() => this._screeningInputParamsService.screeningInputParams());
  public dateFormats = DateFormats;
  private _screeningInputParamsService = inject(ScreeningInputParamsService);
  private _companyLegalRightService = inject(COMPANY_LEGAL_RIGHT_API_SERVICE_TOKEN);
  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });
  private _companyLegalRightCompositeLoadingId = computed(() => `${this.constructor.name}_companyLegalRightComposite`);
  private _personsLoadingId = computed(() => `${this.constructor.name}_persons`);

  public companyLegalRightComposites$!: Observable<CompanyLegalRightCompositeEntity[]>;
  public persons$!: Observable<PersonEntity[]>;

  public ngOnInit(): void {
    const screeningInputParams = this.screeningEntitiesInputParams();
    if (!screeningInputParams) {
      return;
    }
    if (this._reportLoadingStateService) {
      this._reportLoadingStateService.setLoadingState(this._companyLegalRightCompositeLoadingId(), true);
    }
    this.companyLegalRightComposites$ = this._companyLegalRightService.companyLegalRightCompositeCache
      .get(screeningInputParams.companyUuid)
      .pipe(
        finalize(() => {
          if (this._reportLoadingStateService) {
            this._reportLoadingStateService.setLoadingState(this._companyLegalRightCompositeLoadingId(), false);
          }
        }),
      );

    this.persons$ = this.companyLegalRightComposites$.pipe(
      map((items) => {
        return items.reduce((res: PersonEntity[], i) => {
          if (i.company_legal_right_person_composites) {
            for (const c of i.company_legal_right_person_composites) {
              res.push(c.person);
            }
          }
          return res;
        }, []);
      }),
      finalize(() => {
        if (this._reportLoadingStateService) {
          this._reportLoadingStateService.setLoadingState(this._personsLoadingId(), false);
        }
      }),
    );
  }
}
